import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import API from "../../api";
import toastr from "toastr";

interface Prefix {
  id: number;
  prefix_name: string;
}

interface Doctor {
  id?: number;
  id_number: string;
  prefix: string;
  d_name: string;
  address: string;
  qualification: string;
  mobile: string;
  email: string;
}

interface DoctorModalProps {
  show: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  doctorData: Doctor | null;
  onSuccess: () => void;
}

const emptyForm: Doctor = {
  id_number: "",
  prefix: "Dr",
  d_name: "",
  address: "",
  qualification: "",
  mobile: "",
  email: "",
};

const DoctorModal: React.FC<DoctorModalProps> = ({
  show,
  onClose,
  mode,
  doctorData,
  onSuccess,
}) => {
  const [prefixes, setPrefixes] = useState<Prefix[]>([]);
  const [formData, setFormData] = useState<Doctor>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [fetchingPrefixes, setFetchingPrefixes] = useState(false);

  useEffect(() => {
    if (!show) return;
    let mounted = true;

    const fetchPrefixes = async () => {
      setFetchingPrefixes(true);
      try {
        const res = await API.get("/doctors/getPrefixes");
        if (mounted) setPrefixes(res.data?.data || []);
      } catch (err) {
        if (mounted) {
          console.error("Failed to load prefixes:", err);
          toastr.error("Failed to load prefixes");
        }
      } finally {
        if (mounted) setFetchingPrefixes(false);
      }
    };

    fetchPrefixes();
    return () => { mounted = false; };
  }, [show]);

  useEffect(() => {
    if (!show) {
      setFormData(emptyForm);
      return;
    }

    if (mode === "edit" && doctorData) {
      setFormData({
        id_number: doctorData.id_number || "",
        prefix: doctorData.prefix || "Dr",
        d_name: doctorData.d_name || "",
        address: doctorData.address || "",
        qualification: doctorData.qualification || "",
        mobile: doctorData.mobile || "",
        email: doctorData.email || "",
      });
    } else {
      setFormData(emptyForm);
    }
  }, [show, mode, doctorData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.id_number.trim() || !formData.d_name.trim()) {
      toastr.error("ID Number and Name are required");
      return;
    }

    setLoading(true);
    try {
      if (mode === "add") {
        await API.post("/doctors/addDoctor", formData);
        toastr.success("Doctor added successfully");
      } else {
        if (!doctorData?.id) {
          toastr.error("Doctor ID is missing");
          return;
        }
        await API.put(`/doctors/updateDoctor/${doctorData.id}`, formData);
        toastr.success("Doctor updated successfully");
      }
      onSuccess();
    } catch (error: any) {
      console.error("Submit error:", error);
      toastr.error(error?.response?.data?.message || `Failed to ${mode} doctor`);
    } finally {
      setLoading(false);
    }
  };

  const prefixOptions = React.useMemo(() => {
    const list = prefixes.map((p) => p.prefix_name);
    if (formData.prefix && !list.includes(formData.prefix)) {
      return [formData.prefix, ...list];
    }
    return list.length > 0 ? list : ["Dr"];
  }, [prefixes, formData.prefix]);

  const modalKey = `${mode}-${doctorData?.id || "new"}`;

  return (
    <Modal 
      key={modalKey}
      show={show} 
      onHide={onClose} 
      size="lg" 
      centered 
      backdrop="static"
    >
      {/* DARK THEME HEADER */}
      <Modal.Header closeButton className="bg-dark text-white border-secondary">
        <Modal.Title>
          {mode === "add" ? "Create New Doctor" : "Edit Doctor"}
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        {/* DARK THEME BODY */}
        <Modal.Body className="bg-dark text-white">
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  ID Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="id_number"
                  value={formData.id_number}
                  onChange={handleChange}
                  required
                  placeholder="Enter ID number"
                  disabled={loading}
                  className="bg-dark text-white border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <div className="d-flex gap-2">
                  {/* Plain HTML select with dark theme */}
                  <select
                    name="prefix"
                    value={formData.prefix}
                    onChange={handleChange}
                    className="form-select bg-dark text-white border-secondary"
                    style={{ width: "100px", flexShrink: 0 }}
                    disabled={fetchingPrefixes || loading}
                  >
                    {fetchingPrefixes ? (
                      <option value="">Loading...</option>
                    ) : (
                      prefixOptions.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))
                    )}
                  </select>
                  <Form.Control
                    type="text"
                    name="d_name"
                    value={formData.d_name}
                    onChange={handleChange}
                    required
                    placeholder="Enter name"
                    disabled={loading}
                    className="bg-dark text-white border-secondary"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter address"
                  disabled={loading}
                  className="bg-dark text-white border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Qualification</Form.Label>
                <Form.Control
                  type="text"
                  name="qualification"
                  value={formData.qualification}
                  onChange={handleChange}
                  placeholder="Enter qualification"
                  disabled={loading}
                  className="bg-dark text-white border-secondary"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Mobile</Form.Label>
                <Form.Control
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="Enter mobile number"
                  disabled={loading}
                  className="bg-dark text-white border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  disabled={loading}
                  className="bg-dark text-white border-secondary"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        {/* DARK THEME FOOTER */}
        <Modal.Footer className="bg-dark border-secondary">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default DoctorModal;